import { useState, useMemo } from "react";
import { useNotifications } from "@/hooks/useNotifications";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { NotificationItem } from "./NotificationItem";
import { NotificationBulkActionsBar } from "./NotificationBulkActionsBar";
import { NotificationSnoozeDialog } from "./NotificationSnoozeDialog";
import { NotificationFilterChips } from "./NotificationFilterChips";
import { CheckCheck, Inbox, AtSign } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function NotificationList() {
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    markAllAsRead,
    bulkMarkAsRead,
    bulkArchive,
    bulkDelete,
    bulkSnooze,
  } = useNotifications();
  
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>("all");

  const handleSelectAll = () => {
    if (selectedIds.size === notifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map((n: any) => n.id)));
    }
  };

  const handleToggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkAction = (action: () => void) => {
    action();
    setSelectedIds(new Set());
  };

  const handleSnooze = (duration: number) => {
    bulkSnooze({
      notificationIds: Array.from(selectedIds),
      durationMinutes: duration,
    });
    setSelectedIds(new Set());
    setSnoozeDialogOpen(false);
  };

  // Filter notifications based on active filter
  const filteredNotifications = useMemo(() => {
    if (activeFilter === "all") return notifications;
    return notifications.filter((n: any) => n.type === activeFilter);
  }, [notifications, activeFilter]);

  // Calculate counts for each notification type
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: notifications.length };
    notifications.forEach((n: any) => {
      counts[n.type] = (counts[n.type] || 0) + 1;
    });
    return counts;
  }, [notifications]);

  return (
    <>
      <div className="flex flex-col max-h-[60vh] sm:max-h-[600px]">
        {/* Filter Chips */}
        <NotificationFilterChips
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          counts={typeCounts}
        />
        
        {/* Header with selection checkbox */}
        <div className="p-4 flex items-center justify-between border-b">
          <div className="flex items-center gap-3">
            {filteredNotifications.length > 0 && (
              <Checkbox
                checked={selectedIds.size === filteredNotifications.length && filteredNotifications.length > 0}
                onCheckedChange={handleSelectAll}
              />
            )}
            <div>
              {selectedIds.size > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {selectedIds.size} selected
                </p>
              ) : unreadCount > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {unreadCount} unread
                </p>
              ) : null}
            </div>
          </div>
          {unreadCount > 0 && selectedIds.size === 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllAsRead()}
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Notifications */}
        <ScrollArea className="h-[400px] sm:h-[450px]">
          {filteredNotifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Inbox className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{activeFilter === "all" ? "No notifications yet" : "No notifications of this type"}</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredNotifications.map((notification: any) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  isSelected={selectedIds.has(notification.id)}
                  onToggleSelect={handleToggleSelect}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <Separator />
            <div className="p-2 flex gap-2">
              {typeCounts.mention > 0 && (
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  size="sm"
                  onClick={() => navigate('/mentions')}
                >
                  <AtSign className="h-4 w-4 mr-2" />
                  Mentions ({typeCounts.mention})
                </Button>
              )}
              <Button 
                variant="ghost" 
                className="flex-1" 
                size="sm"
                onClick={() => navigate('/notifications')}
              >
                View all
              </Button>
            </div>
          </>
        )}
      </div>

      <NotificationBulkActionsBar
        selectedCount={selectedIds.size}
        onClearSelection={() => setSelectedIds(new Set())}
        onMarkAsRead={() => handleBulkAction(() => bulkMarkAsRead(Array.from(selectedIds)))}
        onArchive={() => handleBulkAction(() => bulkArchive(Array.from(selectedIds)))}
        onDelete={() => handleBulkAction(() => bulkDelete(Array.from(selectedIds)))}
        onSnooze={() => setSnoozeDialogOpen(true)}
      />

      <NotificationSnoozeDialog
        open={snoozeDialogOpen}
        onOpenChange={setSnoozeDialogOpen}
        onConfirm={handleSnooze}
        notificationCount={selectedIds.size}
      />
    </>
  );
}
